import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, CircularProgress, Dialog, DialogContent,
  DialogTitle, IconButton, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { getConversationThread } from "../../services/ops/opsservices";
import {
  BLUE, GREEN, AMBER, RED, GRAY, NAVY,
  StatusChip, fmtTime, fmtDuration, fmtPhone, fullName, safeNum,
} from "./opsShared";

/**
 * The whole conversation for one customerdataid, with the payment ledger
 * alongside it.
 *
 * ── WHY BOTH, ON ONE SCREEN ──
 * The question every Duplicate Watch row raises is "what did the customer
 * actually experience?" A ledger row says money moved. A thread says what was
 * said. Neither alone answers it — the interesting case is a confirmation
 * number in the ledger that appears nowhere in the thread, which reads as an
 * ordinary conversation until you put the two side by side.
 *
 * Events carry confirmationDelivered from the server, computed by looking for
 * the confirmation number in the conversation text. A red "Never sent" chip is
 * the thing a reviewer is here to find.
 */
export default function ConversationDialog({ open, onClose, customerdataid, subtitle }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const load = useCallback(async () => {
    if (!customerdataid) return;
    setLoading(true);
    setError("");
    try {
      setData(await getConversationThread(customerdataid));
    } catch (e) {
      console.error(e);
      setError("Could not load this conversation.");
    } finally {
      setLoading(false);
    }
  }, [customerdataid]);

  useEffect(() => {
    if (open) load();
    else { setData(null); setError(""); }
  }, [open, load]);

  const h      = data?.header || {};
  const turns  = data?.turns  || [];
  const events = data?.events || [];
  const undelivered = events.filter((e) => safeNum(e.confirmationDelivered) === 0);

  // The opening turn's "customer message" is the seeded greeting the campaign
  // starts with, not something the customer typed. Rendering it on the left
  // makes the thread read as if they opened the conversation, so the bubble is
  // dropped and only the outbound reply on that turn is shown.
  //
  // Keyed off position, not isInitial: the flag is not reliably set on the
  // thread rows coming back from getConversationThread, and a turn 0 whose
  // reply is the campaign blast is the seed regardless of what the flag says.
  const isSeedTurn = (t, i) => i === 0;

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxHeight: "88vh" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          gap: 2, px: 2.5, py: 1.75,
          borderBottom: "0.5px solid", borderColor: "divider",
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
              {fullName(h)}{" "}
              <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
                · {h.accountId || "—"}
              </Box>
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
              {fmtPhone(h.phone)}
              {h.daysPastDue != null && ` · ${h.daysPastDue} days past due`}
              {h.totalAmountDue != null && ` · $${Number(h.totalAmountDue).toLocaleString()} due`}
              {subtitle && ` · ${subtitle}`}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <StatusChip
              label={safeNum(h.conversationEnded) === 1 ? "Chat ended" : "Chat open"}
              tone={safeNum(h.conversationEnded) === 1 ? "default" : "ok"}
            />
            <IconButton size="small" onClick={onClose}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Loading conversation…
            </Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {!loading && data && (
          <>
            {/* ── Payment ledger ── */}
            {events.length > 0 && (
              <Box sx={{ px: 2.5, pt: 2 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 500, color: "text.secondary",
                  textTransform: "uppercase", letterSpacing: "0.06em", mb: 1,
                }}>
                  Payment Events ({events.length})
                </Typography>

                {undelivered.length > 0 && (
                  <Alert severity="error" sx={{ mb: 1.5, fontSize: 12, py: 0.5 }}>
                    {undelivered.length}{" "}
                    {undelivered.length === 1 ? "transaction was" : "transactions were"}{" "}
                    processed but the confirmation never reached the customer. Verify
                    against the PNM before contacting them.
                  </Alert>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 2 }}>
                  {events.map((e, i) => {
                    const delivered = safeNum(e.confirmationDelivered) === 1;
                    return (
                      <Box key={i} sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 1.5, py: 1, borderRadius: 1.5,
                        border: "0.5px solid",
                        borderColor: delivered ? "divider" : `${RED}55`,
                        background: delivered ? "transparent" : `${RED}0D`,
                      }}>
                        <StatusChip label={e.eventType}
                          tone={e.eventType === "Payment" ? "ok" : "default"} />
                        {/* TotalAmount is what the card was charged; the fee
                            is broken out because on a duplicate it is what
                            distinguishes two identical charges from two
                            different ones. */}
                        <Typography sx={{ fontSize: 13, fontWeight: 500, minWidth: 110 }}>
                          {e.totalAmount != null ? `$${Number(e.totalAmount).toFixed(2)}` : "—"}
                          {Number(e.feeAmount) > 0 && (
                            <Box component="span" sx={{
                              fontSize: 10, color: "text.disabled", fontWeight: 400, ml: 0.5,
                            }}>
                              incl ${Number(e.feeAmount).toFixed(2)} fee
                            </Box>
                          )}
                        </Typography>
                        <Typography sx={{
                          fontSize: 12, color: "text.secondary",
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}>
                          #{e.confirmationNumber || "none"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "text.disabled", ml: "auto" }}>
                          {fmtTime(e.eventAtUtc)}
                        </Typography>
                        <StatusChip
                          label={delivered ? "Confirmed to customer" : "Never sent"}
                          tone={delivered ? "ok" : "bad"}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* ── Thread ── */}
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <Typography sx={{
                fontSize: 11, fontWeight: 500, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.06em", mb: 1,
              }}>
                Conversation ({turns.length} {turns.length === 1 ? "turn" : "turns"})
              </Typography>

              {turns.length === 0 && (
                <Typography sx={{ fontSize: 12, color: "text.disabled", py: 2 }}>
                  No messages on this conversation.
                </Typography>
              )}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {turns.map((t, i) => {
                  const seed     = isSeedTurn(t, i);
                  const attempts = safeNum(t.deliveryAttempts);
                  const secs     = t.responseSeconds;
                  // Colour the reply by how it was delivered, not by content:
                  // a repeat delivery is the thing being investigated.
                  const replyTone =
                    attempts >= 2 ? RED
                    : secs != null && secs > 60 ? AMBER
                    : GREEN;
                  return (
                    <Box key={t.turnId}>
                      {/* Customer — hidden on the seeded opening turn. */}
                      {t.customerMessage != null && !seed && (
                        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 0.5 }}>
                          <Box sx={{
                            maxWidth: "78%", px: 1.5, py: 1,
                            borderRadius: "12px 12px 12px 2px",
                            background: "rgba(0,0,0,0.04)",
                          }}>
                            <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                              {t.customerMessage}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: "text.disabled", mt: 0.5 }}>
                              {fmtTime(t.receivedUtc)}
                              {safeNum(t.isInitial) === 1 && " · outbound campaign message"}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Agent */}
                      {t.agentReply != null && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Box sx={{
                            maxWidth: "78%", px: 1.5, py: 1,
                            borderRadius: "12px 12px 2px 12px",
                            background: `${BLUE}12`,
                            border: "0.5px solid",
                            borderColor: attempts >= 2 ? `${RED}55` : "transparent",
                          }}>
                            <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                              {t.agentReply}
                            </Typography>
                            <Box sx={{
                              display: "flex", alignItems: "center", gap: 1,
                              mt: 0.5, flexWrap: "wrap",
                            }}>
                              <Typography sx={{ fontSize: 10, color: "text.disabled" }}>
                                {fmtTime(t.repliedUtc)}
                              </Typography>
                              {secs != null && (
                                <Typography sx={{ fontSize: 10, color: replyTone }}>
                                  {fmtDuration(secs)}
                                </Typography>
                              )}
                              {attempts >= 2 && (
                                <StatusChip label={`${attempts} delivery attempts`} tone="bad" />
                              )}
                              {t.stage && t.stage !== "Done" && (
                                <StatusChip label={t.stage} tone="warn" />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {/* A turn with no reply at all — the customer got silence.
                          Not shown for the seeded opener: with its bubble
                          hidden there is nothing that turn was answering. */}
                      {t.agentReply == null && !seed && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Box sx={{
                            px: 1.5, py: 0.75, borderRadius: "12px 12px 2px 12px",
                            border: "0.5px dashed", borderColor: `${RED}77`,
                          }}>
                            <Typography sx={{ fontSize: 12, color: RED }}>
                              No reply sent{t.stage ? ` · ${t.stage}` : ""}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}